import { useState, type ReactElement } from "react";
import { ArrowRight, Check, CircleDashed } from "lucide-react";

import { cn } from "@/lib/utils";

import type { WorkspaceTodo } from "./api";

const VISIBLE_TODO_LIMIT = 4;

export function WorkspaceTodoList({
  todos,
}: {
  todos: WorkspaceTodo[];
}): ReactElement | null {
  const [expanded, setExpanded] = useState(false);
  if (todos.length === 0) return null;

  const completed = todos.filter((todo) => todo.status === "completed").length;
  const hiddenCount = Math.max(0, todos.length - VISIBLE_TODO_LIMIT);
  const visible = expanded ? todos : todos.slice(0, VISIBLE_TODO_LIMIT);

  return (
    <div
      className="mt-3 overflow-hidden rounded-xl border border-[#d9e2de] bg-white"
      aria-label={`To-dos ${completed}/${todos.length}`}
    >
      <div className="border-b border-[#eef2f0] px-3 py-2 text-xs font-semibold text-[#29312e]">
        To-dos {completed}/{todos.length}
      </div>
      <ol className="divide-y divide-[#f2f5f3]">
        {visible.map((todo) => (
          <TodoRow key={todo.id} todo={todo} />
        ))}
      </ol>
      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-[#5b716b] hover:bg-[#f7faf8]"
        >
          <span aria-hidden="true" className="text-[#8aa19a]">
            ···
          </span>
          {expanded ? "Show less" : `${hiddenCount} more`}
        </button>
      ) : null}
    </div>
  );
}

function TodoRow({ todo }: { todo: WorkspaceTodo }): ReactElement {
  const current = todo.status === "in_progress";
  const done = todo.status === "completed";
  return (
    <li className="flex items-start gap-2.5 px-3 py-2">
      <span
        className={cn(
          "mt-0.5 flex size-4 shrink-0 items-center justify-center",
          done
            ? "text-[#3d7a6b]"
            : current
              ? "text-[#315d54]"
              : "text-[#9aada7]",
        )}
        aria-hidden="true"
      >
        {done ? (
          <Check className="size-3.5" strokeWidth={2.5} />
        ) : current ? (
          <ArrowRight className="size-3.5" strokeWidth={2.5} />
        ) : (
          <CircleDashed className="size-3.5" />
        )}
      </span>
      <span
        className={cn(
          "min-w-0 text-xs leading-5",
          done
            ? "text-[#7b8883] line-through"
            : current
              ? "font-medium text-[#24352f]"
              : "text-[#4d5b56]",
        )}
      >
        {todo.content}
      </span>
    </li>
  );
}
